// 여러 화면이 같이 쓰는 작은 조각 — AI 사용 정도 표시, 로그인 안내, 오류 문구, 불러오는 중
import { useState } from 'react';

import { usePluginMessages, useSession } from '../../../composition/plugin-runtime';
import { describeError } from '../composition/use-serial';
import type { AiUsage } from '../domain/serial';
import { Tag, type TagVariant } from './whif/shared/ui/tag';
import { WhifButton } from './whif/shared/ui/whif-button';

// 없음 → 회색 · 보조 → 브랜드 옅게 · 생성 → 브랜드 짙게. 정도가 커질수록 눈에 띄게.
// 세 배지는 같은 character-* 계열(크기·모양 동일)로 두고, 없음만 색을 중립으로 덮는다 — hashtag 계열은 # 를 붙여서 쓰지 않는다
const TAG: Readonly<Record<AiUsage, { variant: TagVariant; tone?: string }>> = {
  // 카드 바탕(serial-surface)이 bg-neutral-weak 와 같은 coolgray-95 라 한 단계 밝은 면을 쓴다
  none: { variant: 'character-relation', tone: 'bg-serial-surface-raised text-text-neutral-subtle' },
  assisted: { variant: 'character-relation' },
  generated: { variant: 'character-main-su' },
};

/** 작품마다 AI 사용 정도를 항상 보인다 — 숨기지 않고 밝히는 것이 이 앱의 약속이다 (DEC-95) */
export function AiBadge({ usage }: { usage: AiUsage }) {
  const t = usePluginMessages('serial');
  const { variant, tone } = TAG[usage];
  return (
    <Tag variant={variant} className={`w-fit shrink-0 ${tone ?? ''}`}>
      {t(`ai.${usage}`)}
    </Tag>
  );
}

/** 쓰기 화면에서 로그인하지 않았으면 이 안내만 보인다. 로그인 버튼은 머리말 오른쪽에 있다(auth 플러그인 app.header) */
export function SignInGate({ children }: { children: React.ReactNode }) {
  const t = usePluginMessages('serial');
  const session = useSession();
  if (session) return <>{children}</>;
  return <p className="py-16 text-center text-sm text-text-neutral-subtle">{t('signInToWrite')}</p>;
}

export function ErrorText({ error }: { error: unknown }) {
  const t = usePluginMessages('serial');
  if (!error) return null;
  const { key, detail } = describeError(error);
  const limit = typeof detail.minChars === 'number' ? detail.minChars : typeof detail.dailyCap === 'number' ? detail.dailyCap : null;
  return (
    <p role="alert" className="text-sm text-serial-error">
      {t(key)}
      {limit !== null ? ` (${limit})` : ''}
    </p>
  );
}

export function Loading() {
  const t = usePluginMessages('serial');
  return <p className="py-16 text-center text-sm text-text-neutral-weak">{t('loading')}</p>;
}

/**
 * 내리기 버튼 — 한 번 누르면 확인 문구와 「내리기」·「취소」가 나오고, 확인을 눌러야 onConfirm 이 돈다.
 * 브라우저 confirm() 대신 화면 안에서 묻는다. 지우지 않고 숨기는 것이라 문구도 「내리기」다
 */
export function ConfirmRemove({ label, question, onConfirm, pending }: { label: string; question: string; onConfirm: () => void; pending: boolean }) {
  const t = usePluginMessages('serial');
  const [asking, setAsking] = useState(false);
  if (!asking) {
    return (
      <WhifButton variant="ghost" size="sm" className="self-start" onClick={() => setAsking(true)}>
        {label}
      </WhifButton>
    );
  }
  return (
    <div role="group" aria-label={question} className="flex flex-wrap items-center gap-2 rounded-global-radius-12 bg-serial-surface p-3">
      <span className="text-sm text-text-neutral-subtle">{question}</span>
      <WhifButton variant="destructive" size="sm" onClick={onConfirm} disabled={pending}>
        {t('remove.confirm')}
      </WhifButton>
      <WhifButton variant="ghost" size="sm" onClick={() => setAsking(false)} disabled={pending}>
        {t('remove.cancel')}
      </WhifButton>
    </div>
  );
}
